import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { apiGet } from 'api/service';

const stateUi = {
  healthy: {
    color: 'success',
    icon: <CheckCircleOutlineIcon fontSize="small" />,
    label: 'Healthy'
  },
  unhealthy: {
    color: 'error',
    icon: <ErrorOutlineIcon fontSize="small" />,
    label: 'Unhealthy'
  },
  disabled: {
    color: 'default',
    icon: <PauseCircleOutlineIcon fontSize="small" />,
    label: 'Disabled'
  }
};

const AdminToolsHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await apiGet('/api/admin/tools/health/');
      setData(payload || null);
    } catch (err) {
      setError(err?.message || 'Failed to load tool health.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const checks = data?.checks || [];

  return (
    <Card>
      <CardHeader
        title="Tool Health"
        subheader="Operational status of scanning engines and workers"
        action={
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
      />
      <CardContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Healthy: ${summary.healthy || 0}`} color="success" size="small" />
              <Chip label={`Unhealthy: ${summary.unhealthy || 0}`} color="error" size="small" />
              <Chip label={`Disabled: ${summary.disabled || 0}`} size="small" />
            </Stack>

            {checks.map((check) => {
              const ui = stateUi[check.state] || stateUi.unhealthy;
              return (
                <Box
                  key={check.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{check.label}</Typography>
                    <Chip
                      size="small"
                      color={ui.color}
                      icon={ui.icon}
                      label={ui.label}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {check.detail || 'No details provided.'}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminToolsHealthCard;
